"use client";

import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const Sla = () => {
  const { t } = useTranslation();
  const lastUpdatedDate = "2026-02-26";

  const renderList = (key: string) => {
    const raw = t(key, { returnObjects: true });
    const items = Array.isArray(raw) ? raw.map((item) => (typeof item === "string" ? item : String(item))) : [];
    if (items.length === 0) return null;
    return (
      <ul className="list-disc pl-5 space-y-1 my-2 text-muted-foreground">
        {items.map((item, i) => (
          <li key={i} className="leading-relaxed">
            {item}
          </li>
        ))}
      </ul>
    );
  };

  const Section = ({
    titleKey,
    children,
  }: {
    titleKey: string;
    children: React.ReactNode;
  }) => (
    <section className="mb-8">
      <h2 className="font-display text-lg font-semibold text-foreground mb-3">
        {String(t(titleKey))}
      </h2>
      <div className="text-muted-foreground leading-relaxed space-y-2">
        {children}
      </div>
    </section>
  );

  const SubSection = ({
    titleKey,
    children,
  }: {
    titleKey: string;
    children: React.ReactNode;
  }) => (
    <div className="mt-4 ml-0">
      <h3 className="font-display text-base font-semibold text-foreground mb-2">
        {String(t(titleKey))}
      </h3>
      <div className="text-muted-foreground leading-relaxed space-y-2">
        {children}
      </div>
    </div>
  );

  const P = ({ keyName }: { keyName: string }) => <p>{String(t(keyName))}</p>;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="pt-28 pb-16 md:pt-32 md:pb-20">
          <div className="container mx-auto px-4 md:px-8 max-w-3xl">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4" />
              {String(t("sc.backHome"))}
            </Link>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-2">
              {String(t("sla.title"))}
            </h1>
            <p className="text-sm text-muted-foreground mb-10">
              {String(t("sla.lastUpdated"))}: {lastUpdatedDate}
            </p>

            <p className="text-muted-foreground leading-relaxed mb-10">
              {String(t("sla.intro"))}
            </p>

            <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none text-foreground">
              <Section titleKey="sla.section1Title">
                <P keyName="sla.section1P1" />
                {renderList("sla.section1List")}
                <P keyName="sla.section1P2" />
              </Section>

              <Section titleKey="sla.section2Title">
                <P keyName="sla.section2P1" />
                {renderList("sla.section2List")}
                <P keyName="sla.section2P2" />
              </Section>

              <Section titleKey="sla.section3Title">
                <P keyName="sla.section3P1" />
                <P keyName="sla.section3P2" />
                <SubSection titleKey="sla.section3_1Title">
                  <P keyName="sla.section3_1P1" />
                  {renderList("sla.section3_1List")}
                </SubSection>
              </Section>

              <Section titleKey="sla.section4Title">
                <div className="overflow-x-auto my-4 rounded-lg border border-border">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="px-4 py-2 font-semibold text-foreground">
                          {String(t("sla.section4ColTier"))}
                        </th>
                        <th className="px-4 py-2 font-semibold text-foreground">
                          {String(t("sla.section4ColHours"))}
                        </th>
                        <th className="px-4 py-2 font-semibold text-foreground">
                          {String(t("sla.section4ColResponse"))}
                        </th>
                        <th className="px-4 py-2 font-semibold text-foreground">
                          {String(t("sla.section4ColChannel"))}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-muted-foreground">
                      <tr className="border-b border-border">
                        <td className="px-4 py-2">{String(t("sla.section4Row1Tier"))}</td>
                        <td className="px-4 py-2">{String(t("sla.section4Row1Hours"))}</td>
                        <td className="px-4 py-2">{String(t("sla.section4Row1Response"))}</td>
                        <td className="px-4 py-2">{String(t("sla.section4Row1Channel"))}</td>
                      </tr>
                      <tr className="border-b border-border">
                        <td className="px-4 py-2">{String(t("sla.section4Row2Tier"))}</td>
                        <td className="px-4 py-2">{String(t("sla.section4Row2Hours"))}</td>
                        <td className="px-4 py-2">{String(t("sla.section4Row2Response"))}</td>
                        <td className="px-4 py-2">{String(t("sla.section4Row2Channel"))}</td>
                      </tr>
                      <tr className="border-b border-border last:border-0">
                        <td className="px-4 py-2">{String(t("sla.section4Row3Tier"))}</td>
                        <td className="px-4 py-2">{String(t("sla.section4Row3Hours"))}</td>
                        <td className="px-4 py-2">{String(t("sla.section4Row3Response"))}</td>
                        <td className="px-4 py-2">{String(t("sla.section4Row3Channel"))}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <P keyName="sla.section4BusinessHours" />
              </Section>

              <Section titleKey="sla.section5Title">
                <P keyName="sla.section5P1" />
                <P keyName="sla.section5P2" />
                <div className="overflow-x-auto my-4 rounded-lg border border-border">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="px-4 py-2 font-semibold text-foreground">
                          {String(t("sla.section5ColSeverity"))}
                        </th>
                        <th className="px-4 py-2 font-semibold text-foreground">
                          {String(t("sla.section5ColDescription"))}
                        </th>
                        <th className="px-4 py-2 font-semibold text-foreground">
                          {String(t("sla.section5ColTarget"))}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-muted-foreground">
                      <tr className="border-b border-border">
                        <td className="px-4 py-2">{String(t("sla.section5Row1Severity"))}</td>
                        <td className="px-4 py-2">{String(t("sla.section5Row1Description"))}</td>
                        <td className="px-4 py-2">{String(t("sla.section5Row1Target"))}</td>
                      </tr>
                      <tr className="border-b border-border">
                        <td className="px-4 py-2">{String(t("sla.section5Row2Severity"))}</td>
                        <td className="px-4 py-2">{String(t("sla.section5Row2Description"))}</td>
                        <td className="px-4 py-2">{String(t("sla.section5Row2Target"))}</td>
                      </tr>
                      <tr className="border-b border-border last:border-0">
                        <td className="px-4 py-2">{String(t("sla.section5Row3Severity"))}</td>
                        <td className="px-4 py-2">{String(t("sla.section5Row3Description"))}</td>
                        <td className="px-4 py-2">{String(t("sla.section5Row3Target"))}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Section>

              <Section titleKey="sla.section6Title">
                {renderList("sla.section6List")}
              </Section>

              <Section titleKey="sla.section7Title">
                <P keyName="sla.section7P1" />
                {renderList("sla.section7List")}
              </Section>

              <Section titleKey="sla.section8Title">
                {renderList("sla.section8List")}
              </Section>

              <Section titleKey="sla.section9Title">
                <P keyName="sla.section9P1" />
                {renderList("sla.section9List")}
              </Section>

              <Section titleKey="sla.section10Title">
                {renderList("sla.section10List")}
              </Section>

              <Section titleKey="sla.section11Title">
                <P keyName="sla.section11P1" />
                <P keyName="sla.section11P2" />
              </Section>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Sla;
